import React, { Component } from 'react';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import TransactionsShuttle from 'components/transactionsShuttle/TransactionsShuttle';
import { ApplicationState } from 'store/rootReducer';
import { updateShuttleActivePage } from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import _ from 'lodash';
import { PanePosition } from 'store/transactionShuttle/TransactionShuttleTypes';
import {
  expandTemplateTransactionAction,
  fetchTransactionsForTemplate,
  moveTemplateTransactions,
  reloadTransactionsForTemplate,
  resetTemplateTransactionShuttle,
  updateTemplateShowPriorityModal,
  updateTemplateShuttleActivePage,
  updateTemplateShuttleSearchTerm,
  updateTemplateShuttleSort,
  updateTemplateShuttleTransaction,
  updateTemplateShuttleTransactionSelection,
} from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import { isPrivateCloud } from 'utils/bzmUserConfigUtil';
import { collapseAllMockServices } from 'store/mockServices/mockServiceActions';
import { Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import { SelectAllType } from 'components/blazeTable/BlazeTable.types';
import { AssignmentActionType } from 'components/transactionsShuttle/TransactionShuttle.types';
import { SortConfiguration } from 'types/rest/paginationTypes';
import TransactionPriorityModal from '../MockServices/Modals/TransactionPriorityModal';
import { MockService, PriorityMode } from 'types/rest/mockServiceTypes';
import { updateMockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import AssignMessageFlowModal from '../MockServices/Modals/AssignMessageFlowModal';

type TemplatesTransactionShuttleProps = {
  template: MockServiceTemplate;
  disabled: boolean;
  transactionType: TransactionType;
};

type AllProps = StateProps & DispatchProps & TemplatesTransactionShuttleProps & WithTranslation;

class TemplatesTransactionShuttle extends Component<AllProps> {
  state = {
    showMessagingModal: false,
    moveTxId: null,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<AllProps>): void {
    const { transactionType } = this.props;
    // Reload left pane only
    if (this.props.leftPaneControls.activePage !== nextProps.leftPaneControls.activePage) {
      this.props.fetchTransactionsForTemplate(this.props.template, 'LEFT', transactionType);
    }

    // Reload right pane only
    if (this.props.rightPaneControls.activePage !== nextProps.rightPaneControls.activePage) {
      this.props.fetchTransactionsForTemplate(this.props.template, 'RIGHT', transactionType);
    }

    if (this.props.template.serviceId !== nextProps.template.serviceId) {
      this.props.reloadTransactionsForTemplate(nextProps.template, transactionType);
    }
  }

  /**
   * Change pagination in one of the panes.
   * @param type
   * @param activePage
   */
  updateActivePageHandler = (type: PanePosition, activePage: number) => {
    this.props.updateTemplateShuttleActivePage(type, activePage);
  };

  sortHandler = (type: PanePosition, sort: SortConfiguration) => {
    this.props.updateTemplateShuttleSort(type, sort);
    this.props.fetchTransactionsForTemplate(this.props.template, type, this.props.transactionType);
  };

  /**
   * Filter in the pane has changed.
   * @param type
   * @param activePage
   */
  filterChangedHandler = _.debounce((type: PanePosition, searchTerm: string) => {
    this.props.updateTemplateShuttleSearchTerm(type, searchTerm);

    //Do load of the pane
    this.props.fetchTransactionsForTemplate(this.props.template, type, this.props.transactionType);
  }, 600);

  /**
   * Change of the transaction selection in the pane.
   * @param type
   * @param transactionIds
   * @param selectAllType
   */
  transactionSelectionChangedHandler = (
    type: PanePosition,
    transactionIds: number[],
    selectAllType?: SelectAllType,
  ) => {
    this.props.updateTemplateShuttleTransactionSelection(type, transactionIds, selectAllType);
  };

  moveTransactionHandler = (assignmentType: AssignmentActionType, txId?: number) => {
    //Show modal for messaging txn
    if (this.props.transactionType === 'MESSAGING' && assignmentType === AssignmentActionType.ADD) {
      this.setState({ showMessagingModal: true, moveTxId: txId });
    } else {
      this.moveTransactions(assignmentType, txId);
    }
  };

  /**
   * Adds or removes transactions from / to virtual service based on assignment type.
   * @param assignmentType - action type
   * @param txId - transaction id
   * @param config - optional config for messaging transaction
   */
  moveTransactions = async (assignmentType: AssignmentActionType, txId?: number, config?: string) => {
    await this.props.moveTemplateTransactions(this.props.template, assignmentType, txId, config);

    // Reset selection
    this.props.updateTemplateShuttleTransactionSelection('LEFT', []);
    this.props.updateTemplateShuttleTransactionSelection('RIGHT', []);
  };

  /**
   * Expands item in the simple table.
   * @param type
   * @param expandItem
   */
  expandTransaction = (type: PanePosition, expandItem: number | null) => {
    this.props.expandTemplateTransactionAction(type, expandItem);
  };

  componentDidMount() {
    this.props.reloadTransactionsForTemplate(this.props.template, this.props.transactionType);
  }

  /**
   * Reload both panes.
   */
  reloadPanes = async () => {
    //Do reload of the panes
    this.props.fetchTransactionsForTemplate(this.props.template, 'LEFT', this.props.transactionType);
    this.props.fetchTransactionsForTemplate(this.props.template, 'RIGHT', this.props.transactionType);

    // Reset selection
    this.props.updateTemplateShuttleTransactionSelection('LEFT', []);
    this.props.updateTemplateShuttleTransactionSelection('RIGHT', []);
  };

  onPriorityChanged = async (priorityMode: PriorityMode) => {
    const updated = _.cloneDeep(this.props.template);
    updated.priorityMode = priorityMode;
    await this.props.updateMockServiceTemplate(updated);
    if (priorityMode === PriorityMode.unique_priority) {
      this.props.updateTemplateShowPriorityModal(true);
    }
  };

  onHidePriorityModal = (updatedEntity: MockService | MockServiceTemplate) => {
    this.props.updateTemplateShowPriorityModal(false);
    if (updatedEntity) {
      //Reload pane & reset selection (if any)
      this.props.updateTemplateShuttleSort('RIGHT', {
        sortKey: 'priority',
        direction: 'asc',
      });
      this.props.fetchTransactionsForTemplate(updatedEntity, 'RIGHT', this.props.transactionType);
      this.props.updateTemplateShuttleTransactionSelection('RIGHT', []);
    }
  };

  render() {
    // Prepare pane titles
    const leftShuttleTitle = this.props.t('mockServices:transactionsShuttle.otherTransactions');
    const rightShuttleTitle = (
      <React.Fragment>
        {this.props.t('mockServices:transactionsShuttle.transactionsInThis')} <strong>Virtual Service Template</strong>
      </React.Fragment>
    );

    return (
      <>
        {this.state.showMessagingModal && (
          <AssignMessageFlowModal
            onFinished={flowName => {
              const txnId = this.state.moveTxId;
              this.setState({ showMessagingModal: false, moveTxId: null });
              this.moveTransactions(AssignmentActionType.ADD, txnId, flowName);
            }}
            mockService={this.props.template as MockService}
            onHide={() => {
              this.setState({ showMessagingModal: false, moveTxId: null });
            }}
          />
        )}

        {this.props.rightPaneControls.showPriorityModal && (
          <TransactionPriorityModal
            context={this.props.template}
            onHide={this.onHidePriorityModal}
            type={this.props.transactionType}
          />
        )}
        <TransactionsShuttle
          leftPaneTitle={leftShuttleTitle}
          rightPaneTitle={rightShuttleTitle}
          disabled={this.props.disabled}
          isMoveLeftInProgress={this.props.isMoveLeftInProgress}
          isMoveRightInProgress={this.props.isMoveRightInProgress}
          context={this.props.template}
          leftPaneControls={this.props.leftPaneControls}
          rightPaneControls={this.props.rightPaneControls}
          leftPaneTransactions={this.props.leftPaneTransactions}
          rightPaneTransactions={this.props.rightPaneTransactions}
          onUpdateTransaction={(type: PanePosition, transaction: Transaction) => {
            this.props.updateTemplateShuttleTransaction(type, transaction);
            // There was an update in transaction, collapse all virtual services, so the change will be visible
            this.props.collapseAllMockServices();
          }}
          changeActivePageInPane={this.updateActivePageHandler}
          filterChangedInPane={this.filterChangedHandler}
          reloadHandler={this.reloadPanes}
          txnSelectionChangedInPane={this.transactionSelectionChangedHandler}
          moveTransactions={this.moveTransactionHandler}
          expandItem={this.expandTransaction}
          isSearchDisabled={isPrivateCloud(this.props.blazeUserConfig)}
          onSort={this.sortHandler}
          onUpdateShowPriorityModalVisibility={() => {
            this.props.updateTemplateShowPriorityModal(true);
          }}
          onPriorityModeChanged={this.onPriorityChanged}
          transactionType={this.props.transactionType}
        />
      </>
    );
  }
}

const mapStateToProps = ({ mockServiceTemplateDetail, blazeUserConfig }: ApplicationState) => ({
  blazeUserConfig: blazeUserConfig.blazeUserConfig,
  leftPaneControls: mockServiceTemplateDetail.shuttle.leftPane,
  rightPaneControls: mockServiceTemplateDetail.shuttle.rightPane,
  leftPaneTransactions: mockServiceTemplateDetail.shuttle.leftPaneTransactions,
  rightPaneTransactions: mockServiceTemplateDetail.shuttle.rightPaneTransactions,
  isMoveRightInProgress: mockServiceTemplateDetail.shuttle.isMoveRightInProgress,
  isMoveLeftInProgress: mockServiceTemplateDetail.shuttle.isMoveLeftInProgress,
});

const mapDispatchToProps = {
  updateShuttleActivePage,
  reloadTransactionsForTemplate,
  expandTemplateTransactionAction,
  fetchTransactionsForTemplate,
  updateTemplateShuttleActivePage,
  updateTemplateShuttleSort,
  resetTemplateTransactionShuttle,
  updateTemplateShuttleTransactionSelection,
  moveTemplateTransactions,
  updateTemplateShuttleSearchTerm,
  updateTemplateShuttleTransaction,
  collapseAllMockServices,
  updateMockServiceTemplate,
  updateTemplateShowPriorityModal,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('mockServiceTemplates')(
  connect(mapStateToProps, mapDispatchToProps)(TemplatesTransactionShuttle),
);
